import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { messages } from './messages';
import style from './style.module.less';
import { SimpleModal } from '../../../../../../components/simple-modal';
import { removePaymentMethod } from '../../../../../../financial-services-ui/api';
import { LogService } from '../../../../../../services/log-service';

const showNotification = (type, content) => {
  NotificationService.showNotification({
    notificationType: type,
    title: <FormattedMessage {...messages.header} />,
    content: <FormattedMessage {...content} />,
    duration: NOTIFICATION_DURATIONS.SHORT,
  });
};

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm
 * @param {import("@shipstation/financial-services-ui-components").StoredPaymentMethod} props.paymentMethod
 * @returns {JSX.Element}
 */
export const RemovePaymentMethodModal = ({ onClose, onConfirm, paymentMethod }) => {
  const { accountNumberPreview, paymentMethod: paymentMethodType } = paymentMethod;
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirmation = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await removePaymentMethod(paymentMethod?.id || '');

      if (!result) {
        showNotification(NOTIFICATION_TYPE_ERROR, messages.deleteError);
        onConfirm();
        return;
      }

      showNotification(NOTIFICATION_TYPE_SUCCESS, messages.deleteSuccess);
      onConfirm();
    } catch (error) {
      LogService.logError(new Error('Error removing payment method', { cause: error }));
      showNotification(NOTIFICATION_TYPE_ERROR, messages.deleteError);
      onConfirm();
    } finally {
      setIsLoading(false);
    }
  }, [paymentMethod, onConfirm]);

  const paymentMethodName = paymentMethod.nickname ?? `${paymentMethodType} ${accountNumberPreview}`;

  return (
    <SimpleModal
      actionButtonTheme={THEME_CONSTANTS.danger}
      actionMessage={messages.confirmButton}
      cancelMessage={messages.cancelButton}
      className={style['remove-modal']}
      headerMessage={messages.header}
      isActionDisabled={isLoading}
      onAction={handleConfirmation}
      onCancel={onClose}
      onClose={onClose}
      size='custom'
    >
      <FlexColumn gap={Gap.Space8}>
        <Text formattedMessageValues={{ paymentMethodName }} textType={TextType.H3} value={messages.title} />
        <Text textType={TextType.Paragraph} value={messages.description} />
      </FlexColumn>
    </SimpleModal>
  );
};
